import axios, { type InternalAxiosRequestConfig } from 'axios'

import { config } from '../config'
import { attachMicroserviceAuthRetry } from '../sso/interceptors/microservice-auth-retry.interceptor'
import { resolveCustomersAccessToken } from '../sso/services/customers-access.service'
import { isSuiteSsoRedirectMode } from '../sso/sso-flags'
import { logWarn } from '../sso/utils/sso-logger'

function getStoredPayRefreshToken(): string {
  const raw = globalThis.sessionStorage.getItem('token') || globalThis.localStorage.getItem('token')
  return raw?.replaceAll('"', '') ?? ''
}

/**
 * Customers API client with automatic auth.
 * In SSO mode always uses identity JWT so existing requestAccessToken call sites keep working.
 */
export const CustomersHttp = axios.create({
  timeout: 9000000,
  baseURL: config.api.customers.baseUrl,
  withCredentials: false
})

CustomersHttp.interceptors.request.use(async (requestConfig: InternalAxiosRequestConfig) => {
  if (isSuiteSsoRedirectMode()) {
    const payAccessToken = await resolveCustomersAccessToken(getStoredPayRefreshToken())
    requestConfig.headers.Authorization = `Bearer ${payAccessToken}`
    return requestConfig
  }

  const hasExplicitAuth =
    typeof requestConfig.headers?.Authorization === 'string' &&
    requestConfig.headers.Authorization.length > 0

  if (hasExplicitAuth) {
    return requestConfig
  }

  try {
    const accessToken = await resolveCustomersAccessToken(getStoredPayRefreshToken())
    requestConfig.headers.Authorization = `Bearer ${accessToken}`
  } catch (error) {
    logWarn('CustomersHttp — could not resolve customers access token', { error })
    throw error
  }

  return requestConfig
})

attachMicroserviceAuthRetry(CustomersHttp)
